import type { ScanCallableEntity } from '@/components/smartai/api/getCallGraph';

export type RiskHeatmapMetrics = {
  inDegree: number;
  outDegree: number;
  betweenness: number;
  callFrequency: number;
  riskScore: number;
  riskColor: string;
  riskTier: 'low' | 'medium' | 'high' | 'critical';
};

type GraphEdge = { source: unknown; target: unknown; relation?: string };

const RISK_COLOR_STOPS: Array<{ t: number; rgb: [number, number, number] }> = [
  { t: 0, rgb: [34, 197, 94] },
  { t: 0.33, rgb: [234, 179, 8] },
  { t: 0.66, rgb: [249, 115, 22] },
  { t: 1, rgb: [239, 68, 68] },
];

function linkEndpointId(endpoint: unknown): string {
  if (endpoint != null && typeof endpoint === 'object' && 'id' in endpoint) {
    return String((endpoint as { id: unknown }).id ?? '');
  }
  return String(endpoint ?? '');
}

function entityRowId(ent: ScanCallableEntity): string {
  const entityId = (ent.entityId ?? '').toString().trim();
  if (entityId) return entityId;
  const docId = (ent._id ?? '').toString().trim();
  if (docId) return docId;
  const ek = (ent.entityKey ?? '').toString().trim();
  const line = ent.lineStart;
  if (ek) return `${ek}:L${line ?? ''}`;
  return `${ent.type ?? 'Unknown'}:${ent.name ?? 'anonymous'}:L${line ?? ''}`;
}

function fanInCount(ent?: ScanCallableEntity): number {
  const raw = ent?.metadata?.fanIn;
  if (Array.isArray(raw)) return raw.filter(Boolean).length;
  return 0;
}

function fanOutCount(ent?: ScanCallableEntity): number {
  const m = ent?.metadata;
  if (!m) return 0;
  if (typeof m.fanOut === 'number') return m.fanOut;
  const raw = m.resolvedFanout ?? m.resolvedFanouts ?? m.rawFanout;
  if (Array.isArray(raw)) return raw.filter(Boolean).length;
  return 0;
}

function percentile(values: number[], p: number): number {
  if (!values.length) return 0;
  const sorted = [...values].sort((a, b) => a - b);
  const idx = Math.min(
    sorted.length - 1,
    Math.max(0, Math.ceil((p / 100) * sorted.length) - 1),
  );
  return sorted[idx] ?? 0;
}

function normalizeMetricMap(values: Map<string, number>): Map<string, number> {
  const positives = [...values.values()].filter(v => v > 0);
  const scale = percentile(positives, 95) || Math.max(...positives, 1);
  const out = new Map<string, number>();
  for (const [id, value] of values) {
    out.set(id, scale > 0 ? Math.min(1, value / scale) : 0);
  }
  return out;
}

function interpolateRiskColor(score: number): string {
  const t = Math.max(0, Math.min(1, score));
  let lower = RISK_COLOR_STOPS[0];
  let upper = RISK_COLOR_STOPS[RISK_COLOR_STOPS.length - 1];
  for (let i = 0; i < RISK_COLOR_STOPS.length - 1; i += 1) {
    if (t >= RISK_COLOR_STOPS[i].t && t <= RISK_COLOR_STOPS[i + 1].t) {
      lower = RISK_COLOR_STOPS[i];
      upper = RISK_COLOR_STOPS[i + 1];
      break;
    }
  }
  const span = upper.t - lower.t || 1;
  const local = (t - lower.t) / span;
  const r = Math.round(lower.rgb[0] + (upper.rgb[0] - lower.rgb[0]) * local);
  const g = Math.round(lower.rgb[1] + (upper.rgb[1] - lower.rgb[1]) * local);
  const b = Math.round(lower.rgb[2] + (upper.rgb[2] - lower.rgb[2]) * local);
  return `rgb(${r}, ${g}, ${b})`;
}

function riskTier(score: number): RiskHeatmapMetrics['riskTier'] {
  if (score >= 0.75) return 'critical';
  if (score >= 0.5) return 'high';
  if (score >= 0.25) return 'medium';
  return 'low';
}

function buildCallsAdjacency(
  nodeIds: Set<string>,
  edges: GraphEdge[],
): Map<string, string[]> {
  const adj = new Map<string, string[]>();
  for (const id of nodeIds) adj.set(id, []);
  for (const edge of edges) {
    if (edge.relation !== 'calls') continue;
    const src = linkEndpointId(edge.source);
    const tgt = linkEndpointId(edge.target);
    if (!nodeIds.has(src) || !nodeIds.has(tgt) || src === tgt) continue;
    adj.get(src)!.push(tgt);
  }
  return adj;
}

/** Brandes betweenness on the directed calls subgraph. */
function computeBetweenness(
  nodeIds: string[],
  adjacency: Map<string, string[]>,
): Map<string, number> {
  const scores = new Map<string, number>();
  for (const id of nodeIds) scores.set(id, 0);

  for (const source of nodeIds) {
    const stack: string[] = [];
    const pred = new Map<string, string[]>();
    const sigma = new Map<string, number>();
    const dist = new Map<string, number>();
    const queue: string[] = [];

    for (const id of nodeIds) {
      pred.set(id, []);
      sigma.set(id, 0);
      dist.set(id, -1);
    }

    sigma.set(source, 1);
    dist.set(source, 0);
    queue.push(source);

    while (queue.length > 0) {
      const v = queue.shift()!;
      stack.push(v);
      for (const w of adjacency.get(v) ?? []) {
        if ((dist.get(w) ?? -1) < 0) {
          dist.set(w, (dist.get(v) ?? 0) + 1);
          queue.push(w);
        }
        if (dist.get(w) === (dist.get(v) ?? 0) + 1) {
          sigma.set(w, (sigma.get(w) ?? 0) + (sigma.get(v) ?? 0));
          pred.get(w)!.push(v);
        }
      }
    }

    const delta = new Map<string, number>();
    for (const id of nodeIds) delta.set(id, 0);

    while (stack.length > 0) {
      const w = stack.pop()!;
      for (const v of pred.get(w) ?? []) {
        const sigmaW = sigma.get(w) ?? 1;
        const contribution =
          sigmaW > 0
            ? ((sigma.get(v) ?? 0) / sigmaW) * (1 + (delta.get(w) ?? 0))
            : 0;
        delta.set(v, (delta.get(v) ?? 0) + contribution);
      }
      if (w !== source) {
        scores.set(w, (scores.get(w) ?? 0) + (delta.get(w) ?? 0));
      }
    }
  }

  return scores;
}

function buildEntityLookup(
  entitiesByFile: Record<string, ScanCallableEntity[]>,
): Map<string, ScanCallableEntity> {
  const byRowId = new Map<string, ScanCallableEntity>();
  for (const list of Object.values(entitiesByFile)) {
    for (const ent of list) {
      byRowId.set(entityRowId(ent), ent);
    }
  }
  return byRowId;
}

export function computeRiskHeatmap(
  nodes: Array<Record<string, unknown>>,
  edges: GraphEdge[],
  entitiesByFile: Record<string, ScanCallableEntity[]>,
): Map<string, RiskHeatmapMetrics> {
  const entityByRowId = buildEntityLookup(entitiesByFile);

  const callableIds = new Set<string>();
  const fileIds = new Set<string>();
  const filePathById = new Map<string, string>();

  for (const node of nodes) {
    const id = String(node.id ?? '');
    const entityType = String(node.entityType ?? node.entity_type ?? '');
    if (!id) continue;
    if (entityType === 'File') {
      fileIds.add(id);
      const fp = String(node.source_file ?? '');
      if (fp) filePathById.set(id, fp);
    } else {
      callableIds.add(id);
    }
  }

  const inDegree = new Map<string, number>();
  const outDegree = new Map<string, number>();
  for (const id of callableIds) {
    inDegree.set(id, 0);
    outDegree.set(id, 0);
  }

  for (const edge of edges) {
    if (edge.relation !== 'calls') continue;
    const src = linkEndpointId(edge.source);
    const tgt = linkEndpointId(edge.target);
    if (!callableIds.has(src) || !callableIds.has(tgt)) continue;
    outDegree.set(src, (outDegree.get(src) ?? 0) + 1);
    inDegree.set(tgt, (inDegree.get(tgt) ?? 0) + 1);
  }

  const callFrequency = new Map<string, number>();
  for (const id of callableIds) {
    const graphFreq = (inDegree.get(id) ?? 0) + (outDegree.get(id) ?? 0);
    const rowId = String(
      nodes.find(n => String(n.id) === id)?.entity_row_id ?? '',
    );
    const ent = rowId ? entityByRowId.get(rowId) : undefined;
    const metaFreq = fanInCount(ent) + fanOutCount(ent);
    callFrequency.set(id, Math.max(graphFreq, metaFreq));
  }

  const callsAdjacency = buildCallsAdjacency(callableIds, edges);
  const betweenness = computeBetweenness(
    [...callableIds],
    callsAdjacency,
  );

  const normIn = normalizeMetricMap(inDegree);
  const normOut = normalizeMetricMap(outDegree);
  const normBetweenness = normalizeMetricMap(betweenness);
  const normFrequency = normalizeMetricMap(callFrequency);

  const metrics = new Map<string, RiskHeatmapMetrics>();

  for (const id of callableIds) {
    const composite =
      0.3 * (normIn.get(id) ?? 0) +
      0.25 * (normOut.get(id) ?? 0) +
      0.25 * (normBetweenness.get(id) ?? 0) +
      0.2 * (normFrequency.get(id) ?? 0);
    metrics.set(id, {
      inDegree: inDegree.get(id) ?? 0,
      outDegree: outDegree.get(id) ?? 0,
      betweenness: betweenness.get(id) ?? 0,
      callFrequency: callFrequency.get(id) ?? 0,
      riskScore: composite,
      riskColor: interpolateRiskColor(composite),
      riskTier: riskTier(composite),
    });
  }

  for (const fileId of fileIds) {
    const fp = filePathById.get(fileId) ?? '';
    const callablesInFile = (entitiesByFile[fp] ?? [])
      .map(ent => {
        const rowId = entityRowId(ent);
        return nodes.find(n => String(n.entity_row_id ?? '') === rowId)?.id;
      })
      .filter(Boolean)
      .map(id => String(id));

    const childScores = callablesInFile
      .map(id => metrics.get(id)?.riskScore ?? 0)
      .filter(score => score > 0);

    const fileScore =
      childScores.length > 0
        ? percentile(childScores, 90)
        : 0;

    metrics.set(fileId, {
      inDegree: 0,
      outDegree: callablesInFile.length,
      betweenness: 0,
      callFrequency: childScores.reduce((sum, s) => sum + s, 0),
      riskScore: fileScore,
      riskColor: interpolateRiskColor(fileScore),
      riskTier: riskTier(fileScore),
    });
  }

  return metrics;
}

export const RISK_HEATMAP_LEGEND = [
  { label: 'Low', color: 'rgb(34, 197, 94)' },
  { label: 'Medium', color: 'rgb(234, 179, 8)' },
  { label: 'High', color: 'rgb(249, 115, 22)' },
  { label: 'Critical', color: 'rgb(239, 68, 68)' },
] as const;
